'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Checkbox } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import type { GridApi, IHeaderParams } from 'ag-grid-community';
import type { HARRequest } from '../../hooks/api/mapHarEntryToHarRequest';

type HeaderCheckboxState = boolean | 'indeterminate';

const headerCheckboxState = ({
  selectedCount,
  displayedCount,
}: {
  selectedCount: number;
  displayedCount: number;
}): HeaderCheckboxState => {
  if (selectedCount === 0 || displayedCount === 0) {
    return false;
  }

  if (selectedCount === displayedCount) {
    return true;
  }

  return 'indeterminate';
};

const readHeaderCheckboxState = (
  api: GridApi<HARRequest>,
): HeaderCheckboxState =>
  headerCheckboxState({
    selectedCount: api.getSelectedRows().length,
    displayedCount: api.getDisplayedRowCount(),
  });

export function RequestsSelectionCheckboxHeader({
  api,
}: IHeaderParams<HARRequest>) {
  const tCommon = useTranslations('common');
  const [checked, setChecked] = useState<HeaderCheckboxState>(() =>
    readHeaderCheckboxState(api),
  );

  useEffect(() => {
    const syncChecked = () => {
      setChecked(readHeaderCheckboxState(api));
    };

    syncChecked();
    api.addEventListener('selectionChanged', syncChecked);

    return () => {
      api.removeEventListener('selectionChanged', syncChecked);
    };
  }, [api]);

  return (
    <Box
      w="full"
      h="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Checkbox
        size="sm"
        checked={checked}
        aria-label={tCommon('selectAll')}
        onClick={(event) => event.stopPropagation()}
        onCheckedChange={(details) => {
          if (details.checked === true) {
            api.selectAll(undefined, 'uiSelectAll');

            return;
          }

          api.deselectAll(undefined, 'uiSelectAll');
        }}
      />
    </Box>
  );
}
